<template>
  <div
    class="global-search-input-component"
    :style="{ '--search-expand-offset': `${inlineExpandOffset}px` }"
  >
    <v-btn
      v-if="!btnHidden"
      class="bar-button button-notifications search-btn"
      height="60"
      depressed
      @click="showTextField"
    >
      <font-awesome-icon color="#FF9100" icon="search" />
    </v-btn>
    <v-text-field
      v-if="!btnHidden && isAbsoluteTextFieldShown"
      v-model="search"
      v-click-outside="onClickOutside"
      :disabled="disabled"
      label="Поиск"
      hide-details="auto"
      :class="[
        'search-text-field',
        'search-text-field-absolute',
        { hidden: disabled },
      ]"
      style="width: 0"
      flat
      solo
      height="60"
      clearable
      autofocus
      @click="handleTextFieldClick"
      @click:clear="handleClear"
      @keydown.enter="emitSearch"
      @input="emitClearIfEmpty"
    >
      <template v-slot:prepend-inner>
        <div class="search-type">
          <search-type-icon
            :search-string="search"
          />
        </div>
      </template>

      <template v-slot:append>
        <v-btn
          depressed
          class="emit-search-btn"
          :class="{ 'emit-search-btn-disabled': !allowSearch }"
          @click="emitSearch"
        >
          <font-awesome-icon
            icon="search"
          />
        </v-btn>
      </template>
    </v-text-field>
    <v-text-field
      v-if="!isBtnShown && !disabled"
      ref="inlineField"
      v-model="search"
      :disabled="disabled"
      class="search-text-field"
      :class="{ 'search-text-field--tight': isInlineTight }"
      label="Поиск"
      hide-details="auto"
      flat
      solo
      height="60"
      clearable
      @click:clear="handleClear"
      @keydown.enter="emitSearch"
      @input="emitClearIfEmpty"
    >
      <template v-slot:prepend-inner>
        <div class="search-type">
          <search-type-icon
            :search-string="search"
          />
        </div>
      </template>
      <template v-slot:append>
        <v-btn
          depressed
          class="emit-search-btn"
          :class="{ 'emit-search-btn-disabled': !allowSearch }"
          @click="emitSearch"
        >
          <font-awesome-icon
            icon="search"
          />
        </v-btn>
      </template>
    </v-text-field>
  </div>
</template>

<script>
  import { ClickOutside } from 'vuetify/es5/directives'
  import SearchTypeIcon from '@/components/icons/SearchTypeIcon.vue'
  import {
    MIN_SEARCH_LENGTH,
    ORDERS_PATH,
    buildOrdersQuery,
    getFilterToPreserve,
    getSearchFromRoute,
    pushOrdersRoute,
  } from '@/util/orders-route'
  import { COMPACT_MIN_WIDTH, COMPACT_MAX_WIDTH } from '@/mixins/compact-screen'

  /** Уже этой ширины окна поиск свёрнут в кнопку (вне компактного десктопа) */
  const COLLAPSE_WIDTH = 1650
  // На компактном десктопе поле развёрнуто, если в шапке под него есть место
  // в 100px помещаются кнопка-лупа и место под курсор, подпись «Поиск» - со 168px
  const INLINE_FIELD_MIN_WIDTH = 100
  const INLINE_FIELD_WITH_LABEL_WIDTH = 168
  /** Поле уже этого при вводе раздвигается влево - в нём не видно набранного */
  const INLINE_FIELD_COMFORT_WIDTH = 300

  export default {
    name: 'SearchInput',
    components: { SearchTypeIcon },
    directives: { ClickOutside },
    props: {
      mobile: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
    },
    data () {
      return {
        isBtnShown: false,
        isAbsoluteTextFieldShown: false,
        search: getSearchFromRoute(this.$route),
        resizeObserver: null,
        // на сколько развёрнутое поле раздвигается влево при вводе
        inlineExpandOffset: 0,
        // поле слишком узкое для подписи «Поиск», пока не раздвинуто
        isInlineTight: false,
      }
    },
    computed: {
      allowSearch () {
        return (this.search?.length || 0) >= MIN_SEARCH_LENGTH
      },
      btnHidden () {
        return !this.isBtnShown || this.mobile || this.disabled
      },
    },
    watch: {
      '$route.query.search' () {
        this.search = getSearchFromRoute(this.$route)
      },
    },
    created () {
      window.addEventListener('resize', this.updateBtnAppearance)
      this.updateBtnAppearance()
    },
    mounted () {
      // Свободное место под поле меняется не только с окном: метрики справа
      // подгружаются позже и сдвигают границу
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.updateBtnAppearance)
        this.resizeObserver.observe(this.$el)
      }
      this.updateBtnAppearance()
    },
    beforeDestroy () {
      window.removeEventListener('resize', this.updateBtnAppearance)
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
      }
    },
    methods: {
      updateBtnAppearance () {
        const showBtn = this.shouldShowBtn()

        if (this.isBtnShown && !showBtn) {
          this.hideTextField()
        }

        this.isBtnShown = showBtn
        this.inlineExpandOffset = showBtn ? 0 : this.calcInlineExpandOffset()
        this.isInlineTight = !showBtn && this.isCompactWidth() &&
          this.$el.clientWidth < INLINE_FIELD_WITH_LABEL_WIDTH
      },
      isCompactWidth () {
        return window.innerWidth >= COMPACT_MIN_WIDTH && window.innerWidth <= COMPACT_MAX_WIDTH
      },
      // Узкое поле раздвигается ровно до левого края соседнего блока и накрывает
      // его целиком - без полоски, торчащей из-под поля. Обычно хватает блока
      // с датой; если поле всё ещё узкое - накрывает и следующий слева
      calcInlineExpandOffset () {
        if (!this.$el || !this.isCompactWidth()) {
          return 0
        }

        const fieldWidth = this.$el.clientWidth
        if (fieldWidth >= INLINE_FIELD_COMFORT_WIDTH) {
          return 0
        }

        const left = this.$el.getBoundingClientRect().left
        let offset = 0
        for (let el = this.$el.previousElementSibling; el; el = el.previousElementSibling) {
          if (!el.offsetWidth) continue

          offset = left - el.getBoundingClientRect().left
          if (fieldWidth + offset >= INLINE_FIELD_COMFORT_WIDTH) break
        }

        return offset > 0 ? offset : INLINE_FIELD_COMFORT_WIDTH - fieldWidth
      },
      shouldShowBtn () {
        // На компактном десктопе блок поиска растянут на всё свободное место
        // шапки (см. стили), и его ширина не зависит от того, кнопка в нём или поле
        if (this.isCompactWidth()) {
          return !this.$el || this.$el.clientWidth < INLINE_FIELD_MIN_WIDTH
        }

        return window.innerWidth <= COLLAPSE_WIDTH
      },
      hideTextField () {
        this.isAbsoluteTextFieldShown = false

        this.$nextTick(() => {
          const textField = document.querySelector(
            '.search-text-field .v-input__slot',
          )

          if (textField) {
            textField.style.width = 'auto'
          }
        })
      },
      showTextField () {
        this.isAbsoluteTextFieldShown = true

        this.$nextTick(() => {
          this.setTextFieldPosition()
        })
      },
      setTextFieldPosition () {
        const searchBtnRect = document
          .querySelector('.search-btn')
          .getBoundingClientRect()

        const textField = document.querySelector('.search-text-field-absolute')
        // Координаты кнопки — от окна, поле позиционируется от шапки. Сверху шапку
        // опускает плашка биллинга, слева сдвигает сайдбар: 260px, 326px на широких
        // экранах, 80px у полоски на компактном десктопе.
        const parentRect = textField.offsetParent
          ? textField.offsetParent.getBoundingClientRect()
          : { top: 0, left: 0 }
        textField.style.left = `${searchBtnRect.right - parentRect.left}px`
        textField.style.top = `${searchBtnRect.top - parentRect.top}px`

        const rect = document
          .querySelector('.notification-btn')
          .getBoundingClientRect()

        document.querySelector(
          '.search-text-field-absolute .v-input__slot',
        ).style.width = `${searchBtnRect.right - rect.right}px`
      },
      onClickOutside () {
        // Берем значение сразу из инпута так как в переменной search значение появляется с задержкой
        if (document.querySelector('.search-text-field-absolute input').value) {
          return
        }

        this.isAbsoluteTextFieldShown = false
      },
      handleTextFieldClick () {
        if (!this.search) {
          this.isAbsoluteTextFieldShown = false
        }
      },
      emitSearch () {
        if (!this.allowSearch) {
          // в узком поле лупа занимает больше половины - тап по ней ставит курсор
          if (this.isInlineTight && this.$refs.inlineField) {
            this.$refs.inlineField.focus()
          }
          return
        }
        pushOrdersRoute(this.$router, buildOrdersQuery({
          search: this.search,
          filter: getFilterToPreserve(this.$route),
        }))
      },
      handleClear () {
        this.search = null
        this.isAbsoluteTextFieldShown = false
        this.clearSearchInRoute()
      },
      emitClearIfEmpty () {
        if (!this.search) {
          this.clearSearchInRoute()
        }
      },
      clearSearchInRoute () {
        if (this.$route.path !== ORDERS_PATH || !getSearchFromRoute(this.$route)) {
          return
        }
        pushOrdersRoute(this.$router, buildOrdersQuery({
          filter: getFilterToPreserve(this.$route),
        }))
      },
    },
  }
</script>

<style scoped lang="less">
.global-search-input-component {
  margin-right: 10px;

  @media (min-width: 1500px) {
    width: 100%;
  }

  // Компактный десктоп: блок занимает всё свободное место между датой и
  // метриками; по его ширине решается, кнопка в нём или развёрнутое поле
  .media-compact-pc({
    flex: 1 1 0;
    min-width: 60px;
    margin-right: 6px;

    // Совсем узкое поле в покое - только кнопка-лупа, без подписи «Поиск»;
    // подпись появляется, когда поле раздвинуто фокусом
    .search-text-field--tight:not(.v-input--is-focused)::v-deep label {
      display: none;
    }

    // Узкое поле при вводе раздвигается влево, поверх блока с датой: иначе
    // в нём не видно набранного. Смещение считает calcInlineExpandOffset
    .search-text-field:not(.search-text-field-absolute) {
      position: relative;
      z-index: 2;
      transition: margin-left 0.2s ease;

      &.v-input--is-focused,
      &.v-input--is-dirty {
        max-width: none;
        margin-left: ~"calc(-1 * var(--search-expand-offset, 0px))";
      }
    }
  });

  .search-btn {
    min-width: 60px;

    ::v-deep {
      .v-btn__content {
        font-size: 21px;
      }
    }
  }
  .bar-button {
    height: 50px;
    font-size: 14px;
    background-color: @color-secondary !important;
    //margin-right: 10px;
    border-radius: 5px;

    .media-xl({
      //margin-right: 20px;
    });
  }

  .search-text-field {
    height: 60px;
    //margin-right: 10px;
    border-radius: 5px;

    &-absolute {
      position: absolute;

      &::v-deep {
        .v-input__control {
          direction: rtl;
        }

        .v-input__slot {
          direction: ltr;
          z-index: 10000;
          transition: width 0.5s;
        }

        .v-input__prepend-inner {
          direction: ltr;
        }
      }
    }

    &::v-deep {
      .v-input__slot {
        background-color: @color-secondary !important;
        height: 50px;
      }

      label {
        color: #c4cdd9 !important;
      }

      .v-input__icon--clear {
        button {
          color: @color-pink !important;
          font-size: 20px;
          margin-right: 14px;
        }

      }

    }

    .emit-search-btn {
      cursor: pointer;
      transition: all 0.3s ease;
      color: @color-primary;
      height: 60px;
      width: 60px;
      min-width: 50px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 21px;
      position: relative;
      margin-right: -12px;
      padding: 0;

      &:hover {
        background: #e3e4e7;
      }

      &:before {
        content: '';
        display: block;
        position: absolute;
        width: 1px;
        top: 20%;
        bottom: 20%;
        left: 0px;
        background-color: #dbdfe6;
        opacity: 1;
      }

      &-disabled {
        //opacity: 0.5;
        cursor: not-allowed;
        color: rgba(0, 0, 0, 0.6);
      }
    }

    .search-type {
      //color: #7a7a7a;
      color: @color-primary;
      margin-right: 7px;

      &-icon {
        transition: all 0.3s ease;
      }
    }

    .media-xl({
      //margin-right: 10px;
      /*max-width: 1500px;
      width: 100%;*/
    });

    .media-lg({
      display: none;
      //margin-right: ;
      width: 100%;
      //margin-bottom: 10px;
    });
  }
}

</style>
